// COMPONENT: ComparisonTableCompare.tsx – toggle filter + Profit Split column (FULL FILE, closed correctly)
// --------------------------------------------------------------------------------------------------
// * Default filter shows only rows with maxDrawdownPercent ≥10 for 1‑Step & 2‑Step.
// * Toggle pills allow switching to "All".
// * Account column removed; Profit Split column added.
// * Sticky bar now includes account size (e.g., "— $100,000").

import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ComparisonCardCompare from "./ComparisonCardCompare";
import PromoCTAButtonCompare from "./PromoCTAButtonCompare";

/* -------------------------------------------------------------------------- */
const GROUP_ORDER = ["Instant", "Rapid", "1-Step", "2-Step", "3-Step"] as const;

type GroupLabel = (typeof GROUP_ORDER)[number];

interface Firm {
  id: string;
  name: string;
  slug?: string;
  logo?: string;
  promo?: {
    percent?: number;
    code?: string;
    validUntil?: string;
    validUntilRemark?: string;
    firstPurchaseOnly?: boolean;
  };
  affiliateLink?: string;
  isBestValue?: boolean;
  features?: {
    profitSplit?: number;
  };
  pricing: Array<{
    accountSize: number;
    fee: number;
    phases: number;
    label?: string;
    maxDrawdownPercent?: number;
    dailyDrawdownPercent?: number;
    leverage?: string;
    profitSplit?: number;
  }>;
  profitSplit?: number;
}

interface Price {
  fee: number;
  accountSize: number;
  maxDrawdownPercent?: number;
  dailyDrawdownPercent?: number;
  leverage?: string;
  label?: string;
  profitSplit?: number;
}

type FirmEntry = {
  firm: Firm;
  price: Price;
};

function groupByEvaluation(firms: Firm[], accountSize: number) {
  const groups: Record<GroupLabel, FirmEntry[]> = {
    Instant: [],
    Rapid: [],
    "1-Step": [],
    "2-Step": [],
    "3-Step": [],
  };

  firms.forEach((firm) => {
    if (!Array.isArray(firm.pricing) || firm.pricing.length !== 1) return;
    const price = firm.pricing[0];
    if (price.accountSize !== accountSize) return;

    if (price.label === "Rapid Challenge") {
      groups.Rapid.push({ firm, price });
      return;
    }

    const phase = price.phases ?? 1;
    const key: GroupLabel = phase === 0 ? "Instant" : phase === 1 ? "1-Step" : phase === 2 ? "2-Step" : "3-Step";
    groups[key].push({ firm, price });
  });

  // Mark best deal in each group
  Object.values(groups).forEach((entries) => {
    let bestIdx = -1;
    let maxSave = 0;
    entries.forEach(({ firm, price }, i) => {
      const save = firm.promo?.percent ? price.fee * (firm.promo.percent / 100) : 0;
      if (save > maxSave) {
        maxSave = save;
        bestIdx = i;
      }
    });
    if (bestIdx !== -1) entries[bestIdx].firm.isBestValue = true;
  });

  return groups;
}

/* -------------------------------------------------------------------------- */
export interface ComparisonTableCompareProps {
  firms: Firm[];
  accountSize: number;
  onGroupChange?: (label: string) => void;
}

const ComparisonTableCompare: React.FC<ComparisonTableCompareProps> = ({ firms, accountSize, onGroupChange }) => {
  const filteredFirms = firms.filter((firm) => firm.id !== 'blueguardian');
  const grouped = groupByEvaluation(filteredFirms, accountSize);
  const hasResults = Object.values(grouped).some((g) => g.length > 0);

  /* Sticky bar */
  const [currentGroup, setCurrentGroup] = useState<GroupLabel | null>(null);
  const sectionRefs = useRef<Record<GroupLabel, HTMLDivElement | null>>({
    Instant: null,
    Rapid: null,
    "1-Step": null,
    "2-Step": null,
    "3-Step": null,
  });

  useEffect(() => {
    if (!onGroupChange) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const first = entries.find((e) => e.isIntersecting);
        if (first?.target) {
          const lbl = first.target.getAttribute("data-label") as GroupLabel;
          if (lbl) {
            setCurrentGroup(lbl);
            onGroupChange(lbl);
          }
        }
      },
      { rootMargin: "-100px 0px -60% 0px", threshold: 0 }
    );
    GROUP_ORDER.forEach((l) => sectionRefs.current[l] && obs.observe(sectionRefs.current[l]!));
    return () => obs.disconnect();
  }, [firms, onGroupChange]);

  if (!hasResults) {
    return <div className="text-center text-yellow-300 py-16">No firms found for ${accountSize.toLocaleString()} account size.</div>;
  }

  return (
  <div className="space-y-12">
    {currentGroup && (
      <div className="sticky top-[64px] z-20 bg-gray-950/90 px-4 py-2 border-b border-yellow-800 text-yellow-300 text-sm font-semibold tracking-wide shadow-sm">
        Currently viewing: <span className="uppercase font-bold">{currentGroup}</span> Evaluation — ${accountSize.toLocaleString()}
      </div>
    )}
    {Object.entries(grouped).map(([group, entries]) => {
      if (!entries.length) return null;
      return (
        <div key={group} ref={(el) => (sectionRefs.current[group as GroupLabel] = el)} className="mb-8">
          <h2 className="text-xl font-bold text-yellow-400 mb-2 sticky top-[112px] bg-gray-950/95 z-20 px-2 py-3 rounded shadow">
            {group} {accountSize ? `— $${accountSize.toLocaleString()}` : ""}
          </h2>
          {/* Desktop Table */}
          <div className="hidden lg:block bg-gray-950 rounded-xl overflow-x-auto border border-yellow-900/70 shadow-md">
            <table className="min-w-full table-fixed text-yellow-300 text-xs lg:text-sm font-semibold">
              <thead className="bg-gray-900">
                <tr className="border-b border-yellow-900">
                  <th className="text-left px-2 py-3 lg:px-3 w-[24%]">Firm</th>
                  <th className="text-left px-2 py-3 lg:px-3 w-[13%]">Max DD</th>
                  <th className="text-left px-2 py-3 lg:px-3 w-[13%]">Daily DD</th>
                  <th className="text-left px-2 py-3 lg:px-3 w-[13%]">Leverage</th>
                  <th className="text-left px-2 py-3 lg:px-3 w-[12%]">Profit Split</th>
                  <th className="text-left px-2 py-3 lg:px-3 w-[12%]">Fee</th>
                  <th className="text-left px-2 py-3 lg:px-3 w-[15%]">Promo</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(({ firm: originalFirm, price }) => {
  // Always use the base id from profile.json for review links
  const firm = { ...originalFirm, id: originalFirm.slug || originalFirm.id };

                  const discountedFee = firm.promo?.percent ? price.fee * (1 - firm.promo.percent / 100) : price.fee;
                  return (
                    <tr key={firm.id} className="border-b border-yellow-900 hover:bg-gray-900/90 transition">
                      {/* Firm */}
                      <td className="flex items-center gap-3 py-4 px-2 lg:px-3">
                        <Link to={`/reviews/${firm.id}`} className="w-[48px] flex items-center justify-start">
                          <img
                            src={firm.logo || '/assets/logos/default-logo.svg'}
                            alt={`${firm.name} logo`}
                            className="max-h-8 w-auto object-contain"
                          />
                        </Link>
                        <div className="flex flex-col leading-tight max-w-[160px]">
                          <Link to={`/reviews/${firm.id}`} className="font-semibold text-yellow-300 hover:underline">
                            {firm.name}
                          </Link>
                          {firm.isBestValue && (
                            <span className="text-xs text-yellow-300 bg-yellow-700/20 rounded px-2 py-0.5 w-max mt-1">
                              🔥 Best Deal
                            </span>
                          )}
                        </div>
                      </td>
                      {/* Max DD */}
                      <td className="text-sm py-4 px-2 lg:px-3">{typeof price.maxDrawdownPercent === 'number' ? `${price.maxDrawdownPercent}%` : price.maxDrawdownPercent || '-'}</td>
                      {/* Daily DD */}
                      <td className="text-sm py-4 px-2 lg:px-3">{typeof price.dailyDrawdownPercent === 'number' ? `${price.dailyDrawdownPercent}%` : price.dailyDrawdownPercent || '-'}</td>
                      {/* Leverage */}
                      <td className="text-sm py-4 px-2 lg:px-3">{price.leverage || '-'}</td>
                      {/* Profit Split */}
                      <td className="text-sm py-4 px-2 lg:px-3">{price.profitSplit ? `${price.profitSplit}%` : '-'}</td>
                      {/* Fee */}
                      <td className="text-sm py-4 px-2 lg:px-3" aria-label={`Fee: ${discountedFee < price.fee ? `${price.fee} discounted to ${discountedFee}` : price.fee}`}>
                        {discountedFee < price.fee ? (
                          <>
                            <s className="text-gray-500">${Math.round(price.fee)}</s>{' '}
                            <span className="text-yellow-300 font-bold">${Math.round(discountedFee)}</span>
                          </>
                        ) : (
                          <span className="text-yellow-300 font-bold">${Math.round(price.fee)}</span>
                        )}
                      </td>
                      {/* Promo CTA */}
                      <td className="py-4 px-2 lg:px-3">
                        <PromoCTAButtonCompare
  firmId={firm.id}
  affiliateLink={firm.affiliateLink}
  code={firm.promo?.code}
  percent={firm.promo?.percent}
  validUntil={firm.promo?.validUntil}
  validUntilRemark={firm.promo?.validUntilRemark}
  firstPurchaseOnly={firm.promo?.firstPurchaseOnly}
  isBestValue={firm.isBestValue}
  fee={price.fee}
/>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4 mt-4">
            {entries.map(({ firm, price }) => (
              <ComparisonCardCompare
                key={firm.id}
                firm={{
                  id: firm.id,
                  name: firm.name,
                  logo: firm.logo,
                  promo: firm.promo,
                  affiliateLink: firm.affiliateLink,
                  isBestValue: firm.isBestValue,
                }}
                price={{
                  fee: price.fee,
                  accountSize: price.accountSize,
                  maxDrawdownPercent: price.maxDrawdownPercent,
                  dailyDrawdownPercent: price.dailyDrawdownPercent,
                  leverage: price.leverage,
                  label: price.label,
                  profitSplit: price.profitSplit,
                }}
              />
            ))}
          </div>
        </div>
      );
    })}
  </div>
);
};

export default ComparisonTableCompare;
export { ComparisonTableCompare };